import type { ApiTypes } from "types"

type KeyIndicator = ApiTypes.Model.IntelligenceReportIndicator
type ReportDetail = ApiTypes.Model.IntelligenceReportDetail
type DetailAction = ApiTypes.Model.IntelligenceReportDetailAction
export type UrgencyCode = "now" | "soon" | "monitor"

export const DOMAIN_LABELS: Record<string, string> = {
  sizing_runtime: "Sizing & Runtime",
  setpoints: "Setpoints",
  humidity: "Humidity",
  indoor_air_quality: "Indoor Air Quality (IAQ)",
}

export const METRIC_LABELS: Record<string, string> = {
  median_cooling_runtime: "Median cooling runtime",
  median_heating_runtime: "Median heating runtime",
  median_total_runtime: "Median total HVAC runtime",
  time_in_auto_mode: "Time in auto mode",
  time_in_cooling_mode: "Time in cooling mode",
  time_in_heating_mode: "Time in heating mode",
  time_in_off_mode: "Time in off mode",
  median_setpoint_error: "Median setpoint error",
  median_temperature_setpoint: "Median thermostat temperature setpoint",
  median_auto_low_temperature_setpoint: "Median auto low temperature setpoint",
  median_auto_high_temperature_setpoint: "Median auto high temperature setpoint",
  median_humidity_setpoint: "Median humidity setpoint",
  median_indoor_dew_point: "Median indoor dew point",
  median_outdoor_dew_point: "Median outdoor dew point",
  outdoor_indoor_dew_point_delta: "Outdoor-indoor dew point difference",
  median_iaq_index: "Median IAQ index",
  estimated_cooling_load: "Estimated cooling load",
  estimated_heating_load: "Estimated heating load",
  estimated_latent_load: "Estimated latent load",
}

export const MEANING_COLORS: Record<string, string> = {
  maxed: "#7F1D1D",
  "very poor": "#7F1D1D",
  high: "#EF4444",
  "target not met": "#EF4444",
  "too humid": "#EF4444",
  "too dry": "#EF4444",
  poor: "#EF4444",
  heavy: "#EF4444",
  "extreme high risk": "#EF4444",
  "extreme low risk": "#EF4444",
  "very humid": "#EF4444",
  "strong drying pressure": "#EF4444",
  "outdoor air much drier than indoors": "#EF4444",
  "outdoor air much wetter than indoors": "#EF4444",
  "too cold": "#EF4444",
  elevated: "#F0D62E",
  medium: "#F59E0B",
  "unusually low": "#F59E0B",
  "unusually high": "#F59E0B",
  moderate: "#F59E0B",
  "slightly low": "#F59E0B",
  "slightly below target": "#F59E0B",
  "drying pressure": "#F59E0B",
  "outdoor air drier than indoors": "#F59E0B",
  "outdoor air slightly drier than indoors": "#F59E0B",
  "outdoor air wetter than indoors": "#F59E0B",
  low: "#22C55E",
  ideal: "#22C55E",
  "very good": "#22C55E",
  good: "#22C55E",
  normal: "#22C55E",
  "target met": "#22C55E",
  "very mild": "#22C55E",
  light: "#22C55E",
  "on target": "#22C55E",
  "for context": "#64748b",
  "similar indoor/outdoor moisture": "#64748b",
  "dominant mode": "#64748b",
  "secondary mode": "#64748b",
  "minor mode": "#64748b",
  "trace mode": "#64748b",
  dry: "#64748b",
}

export const HUMIDITY_SETPOINT_OVERRIDES: Record<string, string> = {
  "unusually high": "#EF4444",
  "extreme high risk": "#7F1D1D",
}

export const MEANING_BG: Record<string, string> = {
  "#7F1D1D": "#FEE2E2",
  "#EF4444": "#FEE2E2",
  "#F0D62E": "#FEF9C3",
  "#F59E0B": "#FEF3C7",
  "#22C55E": "#DCFCE7",
  "#64748b": "#F1F5F9",
}

export const URGENCY_BORDER_COLORS: Record<string, string> = {
  now: "#EF4444",
  soon: "#F59E0B",
  monitor: "#6366F1",
}

export const URGENCY_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  now: { bg: "#FEE2E2", text: "#B91C1C" },
  soon: { bg: "#FEF3C7", text: "#B45309" },
  monitor: { bg: "#EEF2FF", text: "#4338CA" },
}

export const LEGEND_ROWS: Array<{ code: string; meaning: string }> = [
  { code: "now", meaning: "Immediate action required to address a comfort, safety, or system risk" },
  { code: "soon", meaning: "Schedule within 1–2 weeks to prevent degradation or recurring issues" },
  { code: "monitor", meaning: "Verify during routine service or future visits" },
]

export const PRIORITY_COLORS: Record<string, { text: string; bg: string; border: string }> = {
  high: { text: "#ef4444", bg: "#fee2e2", border: "#fca5a5" },
  medium: { text: "#f59e0b", bg: "#fef3c7", border: "#fcd34d" },
  low: { text: "#22c55e", bg: "#dcfce7", border: "#86efac" },
}

export const ROUTINE_MAINTENANCE_ACTIONS = new Set(["maintain_filters_and_schedule_service"])

export const DOMAIN_ORDER = ["sizing_runtime", "setpoints", "humidity", "indoor_air_quality"]

export const DEVICE_INFO_FIELDS: Array<{ key: string; label: string }> = [
  { key: "install_date", label: "Install Date" },
  { key: "physical_status", label: "Physical Status" },
  { key: "zip_code", label: "Zip Code" },
  { key: "residence_type", label: "Resident Type" },
  { key: "where_installed", label: "Where Installed" },
  { key: "firmware_version", label: "Firmware Version" },
]

export const SYSTEM_INFO_FIELDS: Array<{ key: string; label: string }> = [
  { key: "system_age", label: "System Age" },
  { key: "auxillary_heating", label: "Auxiliary Heating" },
  { key: "cooling_stages", label: "Cooling Stage(s)" },
  { key: "heating_stages", label: "Heating Stage(s)" },
  { key: "system_type", label: "System Type" },
  { key: "system_stages", label: "System Stages" },
]

export const getMeaningColor = (meaningWord: string, metric?: string): string | undefined => {
  const word = meaningWord.toLowerCase()
  if (metric === "median_humidity_setpoint" && HUMIDITY_SETPOINT_OVERRIDES[word]) {
    return HUMIDITY_SETPOINT_OVERRIDES[word]
  }
  return MEANING_COLORS[word] || undefined
}

export const formatActionTitle = (actionId: string): string =>
  actionId.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase())

export const formatMeaning = (ind: KeyIndicator): string => {
  if (ind.meaning_range) {
    return `${ind.meaning_word} (${ind.meaning_range})`
  }
  return `(${ind.meaning_word})`
}

export const formatPriorityNum = (num: number): string => {
  if (Number.isInteger(num)) return String(num)
  return num.toFixed(1)
}

export const formatContextValue = (value: unknown): string => {
  if (value === null || value === undefined || value === -1) return "—"
  if (typeof value === "boolean") return value ? "true" : "false"
  return String(value)
}

export function groupByDomain(indicators: KeyIndicator[]): Record<string, KeyIndicator[]> {
  const result: Record<string, KeyIndicator[]> = {}
  for (const ind of indicators) {
    const key = ind.domain_code
    if (!result[key]) result[key] = []
    result[key].push(ind)
  }
  return result
}

const SUMMARY_DOMAIN_ORDER = ["overall", "sizing_runtime", "setpoints", "humidity", "indoor_air_quality"]

export const getSummaryBullets = (report: ReportDetail): string[] => {
  const bullets: string[] = []
  for (const domain of SUMMARY_DOMAIN_ORDER) {
    const items = report.detail_summaries.filter(s => s.domain_code === domain)
    for (const item of items) {
      bullets.push(item.assessment)
    }
  }
  return bullets
}

export const getPriorityColors = (priority: string) =>
  PRIORITY_COLORS[priority as keyof typeof PRIORITY_COLORS] || PRIORITY_COLORS.low

const URGENCY_ORDER: UrgencyCode[] = ["now", "soon", "monitor"]

export const getActionsByUrgency = (report: ReportDetail): Array<{ code: UrgencyCode; items: DetailAction[] }> =>
  URGENCY_ORDER.map(code => ({
    code,
    items: report.actions.filter(a => a.urgency === code),
  })).filter(g => g.items.length > 0)

export const hasNowOrSoonActions = (report: ReportDetail): boolean =>
  report.actions.some(a => a.urgency === "now" || a.urgency === "soon")

export const splitMonitorActions = (
  items: DetailAction[],
): { diagnostic: DetailAction[]; routine: DetailAction[] } => ({
  diagnostic: items.filter(a => !ROUTINE_MAINTENANCE_ACTIONS.has(a.action)),
  routine: items.filter(a => ROUTINE_MAINTENANCE_ACTIONS.has(a.action)),
})
